import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CircularProgress
} from '@mui/material';
import { useTranslation } from '../hooks/useTranslation';

const ForgotPassword: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [step, setStep] = useState<'request' | 'verify'>('request');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetDone, setResetDone] = useState(false);

  const validateEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = async (e: React.FormEvent) => {
    console.log('🚀 FRONTEND: Form submitted with email:', email);
    e.preventDefault();
    setError('');
    setMessage('');
    if (!validateEmail(email)) {
      console.log('❌ FRONTEND: Email validation failed');
      setError(t('errors.invalid_email'));
      return;
    }
    
    console.log('✅ FRONTEND: Validation passed, sending request...');
    console.log('🌐 FRONTEND: API URL:', `/api/forgot-password`);
    setLoading(true);
    
    try {
      console.log('📡 FRONTEND: Making fetch request...');
      const response = await fetch(`/api/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      console.log('📥 FRONTEND: Got response:', response.status, response.statusText);
      const data = await response.json();
      console.log('📄 FRONTEND: Response data:', data);

      if (response.ok) {
        console.log('✅ FRONTEND: Success!');
        setMessage(data.message);
        setStep('verify');
      } else {
        console.log('❌ FRONTEND: Error response');
        setError(data.error || t('errors.failed_to_reset_password'));
      }
    } catch (error) {
      console.log('💥 FRONTEND: Exception caught:', error);
      setError(t('errors.network_error'));
    } finally {
      console.log('🏁 FRONTEND: Request completed');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-8">
      <div className="w-full max-w-md mx-auto bg-gray-800/50 backdrop-blur-lg border border-gray-700 rounded-lg p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">
            {t('password_reset.forgot_password_title')}
          </h1>
          <p className="text-gray-400">{t('password_reset.forgot_password_subtitle')}</p>
        </div>
        
        {error && (
          <div className="bg-red-500/20 border border-red-500/50 text-red-300 px-4 py-3 rounded-md mb-6">
            {error}
          </div>
        )}
        
        {message && (
          <div className="bg-green-500/20 border border-green-500/50 text-green-400 px-4 py-3 rounded-md mb-6">
            {message}
          </div>
        )}
        
        {step === 'request' ? (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t('auth.email_label')}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
                placeholder={t('auth.email_placeholder')}
                required
              />
              <p className="text-xs text-gray-400 mt-1">
                {t('password_reset.forgot_password_subtitle')}
              </p>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:from-gray-600 disabled:to-gray-600 text-white font-semibold py-3 px-4 rounded-md transition-all duration-300 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : t('password_reset.send_reset_link')}
            </button>
          </form>
        ) : (
          <form className="space-y-6" onSubmit={async (e) => {
            e.preventDefault();
            setError('');
            setMessage('');
            setLoading(true);
            try {
              const resp = await fetch('/api/reset-password', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token: code, newPassword }),
              });
              const data = await resp.json();
              if (resp.ok) {
                setMessage(data.message);
                setResetDone(true);
              } else {
                setError(data.error || t('errors.failed_to_reset_password'));
              }
            } catch (e) {
              setError(t('errors.network_error'));
            } finally {
              setLoading(false);
            }
          }}>
            {!resetDone && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Код из письма</label>
                  <input value={code} onChange={(e)=>setCode(e.target.value)} className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-md text-white" placeholder="6-значный код" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Новый пароль</label>
                  <input type="password" value={newPassword} onChange={(e)=>setNewPassword(e.target.value)} className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-md text-white" placeholder="Новый пароль" required />
                </div>
              </>
            )}
            <button
              type={resetDone ? 'button' : 'submit'}
              onClick={resetDone ? () => navigate('/login') : undefined}
              disabled={loading}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:from-gray-600 disabled:to-gray-600 text-white font-semibold py-3 px-4 rounded-md transition-all duration-300 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : (resetDone ? 'Войти / Login' : 'Сменить пароль')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword; 